/**
 * Discover's pure functions and copy.
 *
 * Everything a test needs to hold lives here rather than in a component body: there is no DOM
 * renderer in this repository, so a rule buried in JSX is a rule with no test. `MEM-015` AC-2 is
 * the reason this file exists at all — the timezone rule is the kind of thing that looks right on
 * one developer's laptop and is wrong for every reader in another zone.
 */

import { ApiRequestError } from '@h2hspark/ui';

/**
 * `MEM-014` AC-1's greeting, the time-of-day half.
 *
 * The prototype's is "Good morning, Deb". The words are the screen's because the screen is the
 * only place that knows the reader's clock; the heading beneath it ("Find your people") is served
 * by the API, so the two halves cannot be edited in one place and forgotten in the other.
 *
 * The name is optional and the sentence works without it, which is the signed-out case
 * (`MEM-019` AC-1) rather than an edge case.
 */
export function greetingFor(now: Date, firstName?: string | null): string {
  const hour = now.getHours();
  const partOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const trimmed = firstName?.trim();
  return trimmed === undefined || trimmed.length === 0
    ? `Good ${partOfDay}`
    : `Good ${partOfDay}, ${trimmed}`;
}

/**
 * **The date, the zone abbreviation and the reader's own zone moved to `@h2hspark/ui`** on
 * 2026-08-30, and are re-exported here so every call site in this app is unchanged.
 *
 * `EVT-010` AC-4 put the member's event page into `MemberEventView` in `packages/ui`, because the
 * organizer's Review step has to render *the same component* and the two live in different
 * applications. These three came with it: a copy here and a copy there would be the **date line**
 * — the most-read line on the page — rendered by two functions that agree until one is edited.
 *
 * There is still exactly one definition of each. See `packages/ui/src/lib/member-event-time.ts`,
 * which carries the `MEM-015` AC-2 reasoning about why the *abbreviation* is compared rather than
 * the zone name, and why an event with no stored zone renders in UTC rather than in the reader's.
 */
export { formatEventDateTime, readerTimezone, timezoneAbbreviation } from '@h2hspark/ui';

/**
 * `MEM-014` AC-6, as a value the screen reads rather than a rule somebody remembers.
 *
 * The prototype draws a heart on every card and an outline heart on the second one. Saving is
 * Phase 2, and the AC "asserts an absence that will be asked for" — so the absence is written
 * down here, next to the code that would otherwise grow the control.
 */
/**
 * Not rendered today — `event-card.tsx` points at it from a comment. It is `NotYetAvailable`-shaped
 * and one import away from being shown, so it is written as copy a member could read: no story id,
 * and no "Phase 2", which is a roadmap word rather than an answer to *when*. `MEM-014` AC-6 is
 * the acceptance criterion asserting this absence, and it is named here rather than in the string.
 */
export const SAVING_AN_EVENT_IS_NOT_BUILT = {
  label: 'Save this event',
  reason:
    'The prototype draws a heart on every card. There is no saved-events list, no save control ' +
    'on the card and none on the detail page — the absence is deliberate and written down, so ' +
    'that the first person to add a bookmark icon finds a decision rather than a silence.',
  stage: 'a later release',
} as const;

/**
 * What the list region says when there is nothing in it.
 *
 * Two states, and telling them apart is the whole job. A search that matched nothing gets
 * `MEM-014` AC-5's sentence, **composed by the API from the query it actually searched with** —
 * so this function never builds that string itself. Nothing on at all is a different claim and
 * gets different words.
 */
/**
 * The Discover filter row's name — what a screen reader announces for the chips. *Event size*, not
 * the prototype's *Atmosphere* (Sumit, 2026-09-27, TW 44216088, `MEM-014` AC-3); the chips are still the atmosphere
 * descriptors, and the query parameter keeps its name.
 */
export const DISCOVER_SIZE_FILTER_LABEL = 'Event size';

export function emptyListCopy(input: {
  readonly emptyMessage: string | null;
  readonly atmosphereLabel: string | null;
}): { readonly title: string; readonly description: string } {
  if (input.emptyMessage !== null) {
    return {
      title: input.emptyMessage,
      description: 'Search matches an event name, a city, an event size or an interest tag.',
    };
  }
  if (input.atmosphereLabel !== null) {
    return {
      title: `Nothing ${input.atmosphereLabel.toLowerCase()} is coming up.`,
      description: 'Try another event size, or clear the filter to see everything that is on.',
    };
  }
  return {
    title: 'No events are on just yet.',
    description:
      'Organizers are still putting evenings together. This is what is published — not everything ' +
      'that exists.',
  };
}

/**
 * **Whether asking for an event again could get a different answer** (`MEM-015` AC-1; TW
 * 44108653).
 *
 * Retryable: the request never got an answer (a network failure, which `fetch` reports as a
 * `TypeError` rather than a status), a timeout, throttling, or anything that went wrong on the
 * server's side. Final: every other `4xx` — the request was answered, and the same request gets
 * the same answer. `404` in particular is the deliberate one answer for a draft, a scheduled event
 * and an event that never existed, and retrying it would only delay the sentence.
 *
 * One function, read by the query's own retry policy and by the page's error state, so the two
 * cannot disagree about which failure is worth a *Try again*.
 */
export function isRetryableEventFailure(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return true;
  const status = error.statusCode;
  return status === 408 || status === 429 || status >= 500;
}

/**
 * What the event page says when it has no event to draw.
 *
 * Three answers rather than two: *not here* for a `404`; a retryable failure, which offers
 * **Try again** because trying again may work; and a final refusal, which does not, because a
 * control that can only fail again is a control that teaches a member to stop trusting controls.
 */
export const EVENT_UNAVAILABLE_COPY = {
  notFoundHeading: 'That event is not here',
  // Deliberately the same message for an event that never existed, one still being written and
  // one not yet published: which of those it is would itself be information about an event the
  // reader has no business knowing exists.
  notFoundBody: 'The link may be wrong, or the organizer may not have published it yet.',
  heading: 'We could not open that event',
  retryableBody: 'Something went wrong on our side. Try again in a moment.',
  retry: 'Try again',
  finalBody: 'This link could not be opened. Find the event on Discover instead.',
} as const;

/** The event page by id. It still opens, and swaps itself to the readable address (`EVT-041` AC-5). */
export function eventDetailPath(eventId: string): string {
  return `/app/events/${encodeURIComponent(eventId)}`;
}

/**
 * Where a card links (`EVT-041` AC-6): the event's readable address, or its id for an event from
 * before `EVT-041` that has none yet. One place, so the list and the organizer's profile agree.
 */
export function eventPagePath(event: {
  readonly eventId: string;
  readonly slug?: string | null | undefined;
}): string {
  return eventDetailPath(event.slug || event.eventId);
}

/**
 * The address the event page should show instead of the one it was opened at (`EVT-041` AC-5), or
 * `null` when it is already right. A link by id opens the page and then reads as the event; the
 * query and the fragment come along, so a link to a section of the page still lands there.
 */
export function readableEventLocation(input: {
  readonly ref: string;
  readonly detail: { readonly eventId: string; readonly slug?: string | null | undefined };
  readonly search: string;
  readonly hash: string;
}): string | null {
  // Absent as well as `null`: an API from before `EVT-041` sends no `slug` at all, and a page that
  // read that as an address would replace a working link with `/app/events/undefined`.
  const { slug } = input.detail;
  if (slug === null || slug === undefined || slug === '' || input.ref === slug) return null;
  return `${eventDetailPath(slug)}${input.search}${input.hash}`;
}

/**
 * Initials for the gradient avatar the prototype puts beside every organizer name.
 *
 * Moved to `@h2hspark/ui` with `MemberEventView` (`EVT-010` AC-4) and re-exported — see the note
 * on `formatEventDateTime` above.
 */
export { initialsOfName } from '@h2hspark/ui';

/**
 * The card's city line — `Providence, RI` — as the 10 September prototype's card writes it
 * beside a map pin. The city and region only, never the street: the card is a list item, and the
 * address is the event page's (`MEM-016`). `null` when the event has neither.
 */
export function cityLine(place: {
  readonly city: string | null;
  readonly region: string | null;
}): string | null {
  const line = [place.city, place.region]
    .filter((part): part is string => part !== null && part.trim().length > 0)
    .join(', ');
  return line.length === 0 ? null : line;
}

/**
 * **The going count a card draws, or nothing** (`MEM-014`'s ruling of 2026-09-11).
 *
 * Sumit kept the prototype's *"N going"* on the card and took the attendee faces off it, under the
 * rules that already say when a going count may show: **none for a signed-out visitor**
 * (`MEM-019` AC-2 — the count is only requested with a session, so it is `undefined` here), and
 * **never a zero shown as if it were a figure**. A zero on a card reads as *nobody is coming*,
 * which is a claim about a room the member is deciding whether to join, and an unannounced one on
 * a new event — so an event with nobody confirmed yet draws no count at all, the same as an event
 * whose count did not arrive.
 */
export function goingCountLabel(goingCount: number | undefined): string | null {
  if (goingCount === undefined || !Number.isFinite(goingCount) || goingCount <= 0) return null;
  return `${String(goingCount)} going`;
}

/**
 * The 5px accent bar across the top of a card, one of the four gradients the prototype draws its
 * Discover cards with — the spark gradient, then three 120° blends of the purple and fuchsia
 * scales.
 *
 * The prototype assigns them per fixture event; nothing about a real event decides a colour, so
 * the choice is a stable function of the id: the same event always wears the same bar, and a
 * list of cards is not four copies of one. Decoration only — it states nothing.
 */
export const CARD_ACCENT_GRADIENTS = [
  'var(--spark-gradient)',
  'linear-gradient(120deg, hsl(var(--purple-400)) 0%, hsl(var(--fuchsia-300)) 100%)',
  'linear-gradient(120deg, hsl(var(--fuchsia-500)) 0%, hsl(var(--purple-500)) 100%)',
  'linear-gradient(120deg, hsl(var(--purple-600)) 0%, hsl(var(--fuchsia-400)) 100%)',
] as const;

export function cardAccentFor(eventId: string): string {
  let hash = 0;
  for (const character of eventId) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return CARD_ACCENT_GRADIENTS[hash % CARD_ACCENT_GRADIENTS.length] ?? CARD_ACCENT_GRADIENTS[0];
}
